import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FormikProps } from 'formik'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import FilterListIcon from '@mui/icons-material/FilterList'
import Chip from '@mui/material/Chip'

import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { ConsultationsSearchFormProps, ConsultationsSearchFormValues } from './ConsultationsSearchForm'
import { ConsultationType } from '@/core/models/consultation.model'

export default function ConsultationsSearchFormMobile(props: ConsultationsSearchFormProps) {
  const { consultationTypes, doctorOptions, initialValues, onReset, onSearchDoctor, onSearchPatient, onSubmit, patientOptions } = props
  const { t } = useTranslation()

  const [anchorEl, setAnchorEl] = useState(null)

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'advanced-filters-popover' : undefined

  const handleSearchDoctor = useCallback(
    (inputValue: string) => {
      onSearchDoctor?.(inputValue)
    },
    [onSearchDoctor]
  )

  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  const toggleConsultationType = (type: ConsultationType, formikProps: FormikProps<ConsultationsSearchFormValues>) => {
    const selectedTypes = formikProps.values.type
    const index = selectedTypes.indexOf(type)

    if (index === -1) {
      formikProps.setFieldValue('type', [...selectedTypes, type])
    } else {
      const updatedSelectedTypes = [...selectedTypes.slice(0, index), ...selectedTypes.slice(index + 1)]
      formikProps.setFieldValue('type', updatedSelectedTypes)
    }
  }

  return (
    <Box sx={{ padding: '0 15px' }}>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {(formikProps) => {
          return (
            <Form onSubmit={formikProps.handleSubmit}>
              <Grid
                container
                sx={{
                  marginTop: '10px',
                }}
                spacing={2}
              >
                <Grid item xs={10}>
                  <AutocompleteField
                    label={t('page.consultations.consultationsSearchForm.field.searchByPatient.label')}
                    name="patient"
                    onInputChange={handleSearchPatient}
                    options={patientOptions}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton aria-label="toggle filters" onClick={handleClick}>
                    <FilterListIcon />
                  </IconButton>
                </Grid>
                <Menu
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem>
                    <AutocompleteField
                      label={t('page.consultations.consultationsSearchForm.field.searchByDoctor.label')}
                      name="doctor"
                      onInputChange={handleSearchDoctor}
                      options={doctorOptions}
                      fullWidth
                    />
                  </MenuItem>
                  <MenuItem>
                    <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateFrom.label')} name="startDate.$gte" />
                  </MenuItem>
                  <MenuItem>
                    <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateTo.label')} name="startDate.$lte" />
                  </MenuItem>
                </Menu>
                <Grid item xs={12}>
                  {consultationTypes.map((type, index) => (
                    <Chip
                      key={`${type}${index}`}
                      label={t(`page.consultations.consultationsSearchForm.field.searchByType.option.${type}.label`)}
                      clickable
                      variant="outlined"
                      color={formikProps.values.type.includes(type) ? 'info' : 'default'}
                      onClick={() => toggleConsultationType(type, formikProps)}
                      sx={{ marginRight: '5px', marginTop: '5px' }}
                    />
                  ))}
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button
                      onClick={() => {
                        formikProps.handleReset()
                        onReset?.(formikProps.initialValues)
                      }}
                      variant="neutral"
                    >
                      {t('common:button.reset.label')}
                    </Button>
                    <LoadingButton id="searchButton" type="submit" variant="contained">
                      {t('common:button.submit.label')}
                    </LoadingButton>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Box>
  )
}
